import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Login from './pages/Login';
import AdminDashboard from './components/Admin/AdminDashboard';
import TeacherDashboard from './components/Teacher/TeacherDashboard';
import Loader from './components/Loader';
import { auth, onAuthStateChanged, signOut } from './firebase';


const AUTH_KEYS = [
  'currentUser',
  'userRole',
  'isAdminLoggedIn',
  'isTeacherLoggedIn',
  'isStudentLoggedIn',
  'adminId',
  'adminName',
  'teacherId',
  'teacherName',
  'studentId',
  'studentName'
];

const clearAuthStorage = () => {
  AUTH_KEYS.forEach((key) => sessionStorage.removeItem(key));
};

const normalizeRole = (role) => {
  if (role === 'lab' || role === 'laborant' || role === 'лаборант') return 'teacher';
  return role;
};

const isValidUser = (user) => {
  if (!user || typeof user !== 'object') return false;
  if (!user.role) return false;

  const role = normalizeRole(user.role);

  if (!['admin', 'teacher'].includes(role)) return false;

  return true;
};

const ProtectedRoute = ({ children, user, requiredRole }) => {
  if (!isValidUser(user)) {
    return <Navigate to="/login" replace />;
  }

  const userRole = normalizeRole(user.role);
  const neededRole = normalizeRole(requiredRole);

  if (neededRole && userRole !== neededRole) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      const savedUser = sessionStorage.getItem('currentUser');

      if (!firebaseUser) {
        if (savedUser) {
          console.warn('Firebase Auth session not found. Logging out...');
          clearAuthStorage();
          setUser(null);
        }
        setLoading(false);
        return;
      }

      // User is signed in in Firebase Auth
      if (savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          if (isValidUser(parsedUser)) {
            setUser(parsedUser);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.error('Error parsing saved user:', e);
        }
      }

      // If user is logged in in Firebase Auth but no sessionStorage user, or invalid role, log out
      clearAuthStorage();
      setUser(null);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = (userData) => {
    if (!isValidUser(userData)) {
      clearAuthStorage();
      setUser(null);
      return;
    }

    setUser(userData);

    sessionStorage.setItem('currentUser', JSON.stringify(userData));
    sessionStorage.setItem('userRole', userData.role);

    sessionStorage.removeItem('isAdminLoggedIn');
    sessionStorage.removeItem('isTeacherLoggedIn');
    sessionStorage.removeItem('isStudentLoggedIn');

    if (userData.role === 'admin') {
      sessionStorage.setItem('isAdminLoggedIn', 'true');
      sessionStorage.setItem('adminId', userData.id || userData.uid || '');
      sessionStorage.setItem('adminName', userData.fullName || userData.name || '');
    }

    if (userData.role === 'teacher' || userData.role === 'lab') {
      sessionStorage.setItem('isTeacherLoggedIn', 'true');
      sessionStorage.setItem('teacherId', userData.id || userData.uid || '');
      sessionStorage.setItem('teacherName', userData.fullName || userData.name || '');
    }
  };

  const handleLogout = () => {
    signOut(auth).catch((err) => console.error('Sign out error:', err));
    setUser(null);
    clearAuthStorage();
  };

  if (loading) {
    return <Loader text="Загрузка портала..." fullPage size={60} />;
  }


  return (
    <Router>
      <div className="app-main">
        <Routes>
          <Route
            path="/login"
            element={<Login onLogin={handleLogin} onLogout={handleLogout} />}
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute user={user} requiredRole="admin">
                <AdminDashboard onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/teacher"
            element={
              <ProtectedRoute user={user} requiredRole="teacher">
                <TeacherDashboard onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/"
            element={
              isValidUser(user) ? (
                <Navigate
                  to={normalizeRole(user.role) === 'admin' ? '/admin' : '/teacher'}
                  replace
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;